import { useNavigate } from "@tanstack/react-router";
import { useEffect, useRef, useState } from "react";
import { api, ApiRequestError, auth, invite } from "../lib/api.ts";
import { wipeDevice } from "../lib/device.ts";
import { GOOGLE_CLIENT_ID, renderGoogleButton } from "../lib/googleId.ts";
import { localeStore, useT } from "../lib/i18n.ts";
import { supabase } from "../lib/supabase.ts";
import { terms } from "../lib/terms.ts";
import { AcceptLine } from "./Legal.tsx";
import { RETURN_TO_KEY } from "./OAuthConsent.tsx";

/**
 * Production: Google only (founder decision 2026-09-02) — rendered on our own
 * origin via Google Identity Services when VITE_GOOGLE_CLIENT_ID is set, else
 * Supabase's hosted OAuth redirect. The invite code is required for now.
 * Dev/CI/E2E builds (no Supabase config): the API's dev login.
 * The only word about sync is the one cloud line (offline is invisible).
 */
export function SignIn() {
  const t = useT();
  const [email, setEmail] = useState("");
  const [inviteCode, setInviteCode] = useState(invite.get() ?? "");
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const gsiEl = useRef<HTMLDivElement>(null);
  // The API bounced us here: signed in, but the private beta needs a code.
  const betaRequired = new URLSearchParams(window.location.search).get("beta") === "required";
  // …or the account was deleted less than 30 days ago (D-33): say so, offer another account.
  const [deleted, setDeleted] = useState(() => new URLSearchParams(window.location.search).get("deleted") === "1");
  const nav = useNavigate();
  const useGis = !!supabase && !!GOOGLE_CLIENT_ID;
  /** Where to go once signed in: an OAuth consent page that sent us here, else Home. */
  const landed = () => {
    let back: string | null = null;
    try { back = localStorage.getItem(RETURN_TO_KEY); if (back) localStorage.removeItem(RETURN_TO_KEY); } catch { /* fine */ }
    if (back && back.startsWith("/oauth/")) { window.location.assign(back); return; }
    void nav({ to: "/rest" });
  };
  const ready = inviteCode.trim().length > 0;

  /** Every way of continuing passes here: keep the invite, and note that the
   *  person continued past the terms line (D-38) — recorded once signed in. */
  function keepInvite() {
    if (inviteCode.trim()) invite.set(inviteCode.trim());
    terms.markAccepted();
  }

  useEffect(() => {
    if (!supabase || betaRequired) return;
    let active = true;
    supabase.auth.getSession().then(({ data }) => {
      if (active && data.session) {
        auth.setToken(data.session.access_token);
        landed();
      }
    });
    const { data: sub } = supabase.auth.onAuthStateChange((_event, session) => {
      if (active && session) {
        auth.setToken(session.access_token);
        landed();
      }
    });
    return () => {
      active = false;
      sub.subscription.unsubscribe();
    };
  }, [nav, betaRequired]);

  // Google's button lives on our origin; it appears once the invite is filled in.
  useEffect(() => {
    if (!useGis || betaRequired || !ready || !gsiEl.current) return;
    let cancelled = false;
    renderGoogleButton(
      gsiEl.current,
      async (idToken, nonce) => {
        if (cancelled) return;
        setError(null);
        setBusy(true);
        keepInvite();
        const { error: err } = await supabase!.auth.signInWithIdToken({ provider: "google", token: idToken, nonce });
        setBusy(false);
        if (err) setError(err.message);
      },
      localeStore.get()
    ).catch((e: Error) => setError(e.message));
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [useGis, betaRequired, ready]);

  /** Signed in but new to the beta: try the code here, so a wrong one is a
   *  message on this screen, not a silent bounce (the API would otherwise
   *  send us straight back to /signin?beta=required). */
  async function redeemInvite() {
    setError(null);
    setBusy(true);
    keepInvite();
    try {
      await api.me(); // consumes the invite server-side on first sight
      landed();
    } catch (e) {
      if (e instanceof ApiRequestError && e.code === "BETA_INVITE_REQUIRED") setError(t("signin.inviteInvalid"));
      else if (e instanceof ApiRequestError && e.code === "ACCOUNT_DELETED") { await wipeDevice(); setDeleted(true); }
      else if (e instanceof ApiRequestError && e.status === 401) { auth.clear(); await nav({ to: "/signin" }); }
      else setError(e instanceof Error ? e.message : "Sign-in failed.");
    } finally {
      setBusy(false);
    }
  }

  async function googleRedirect() {
    setError(null);
    keepInvite();
    const { error: err } = await supabase!.auth.signInWithOAuth({ provider: "google", options: { redirectTo: `${window.location.origin}/signin` } });
    if (err) setError(err.message);
  }

  async function devLogin(e: React.FormEvent) {
    e.preventDefault();
    try {
      keepInvite();
      const { token } = await api.devLogin(email, "", inviteCode.trim() || undefined);
      auth.setToken(token);
      await nav({ to: "/rest" });
    } catch (err) {
      if (err instanceof ApiRequestError && err.code === "ACCOUNT_DELETED") setError(t("signin.deleted"));
      else setError(err instanceof Error ? err.message : "Sign-in failed.");
    }
  }

  const inviteField = (
    <div className="field">
      <label htmlFor="invite">{t("signin.invite")}</label>
      <input id="invite" className="input" type="text" required={!!supabase || betaRequired} value={inviteCode} onChange={(e) => setInviteCode(e.target.value)} autoComplete="off" autoCapitalize="characters" />
    </div>
  );

  return (
    <main className="shell-main" style={{ display: "flex", flexDirection: "column", justifyContent: "center", minHeight: "100vh", gap: "var(--sp-5)" }}>
      <div className="signin-brand">
        <div className="display" style={{ color: "var(--accent)" }}>instead</div>
        <p className="meta" style={{ marginTop: "var(--sp-2)" }}>{t("signin.tagline")}</p>
      </div>

      {supabase ? (
        betaRequired ? (
          <form onSubmit={(e) => { e.preventDefault(); void redeemInvite(); }}>
            {inviteField}
            <button className="btn btn-primary btn-block" type="submit" disabled={!ready || busy}>{t("signin.continue")}</button>
          </form>
        ) : (
          <div style={{ display: "flex", flexDirection: "column", gap: "var(--sp-4)" }}>
            {inviteField}
            {useGis ? (
              <div ref={gsiEl} aria-busy={busy} style={{ display: "flex", justifyContent: "center", minHeight: 44, opacity: ready ? 1 : 0.4 }} />
            ) : (
              <button className="btn btn-primary btn-block" type="button" onClick={googleRedirect} disabled={!ready}>{t("signin.google")}</button>
            )}
          </div>
        )
      ) : (
        <form onSubmit={devLogin}>
          <div className="field">
            <label htmlFor="email">{t("signin.email")}</label>
            <input id="email" className="input" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} autoComplete="email" />
          </div>
          {betaRequired && inviteField}
          <button className="btn btn-primary btn-block" type="submit">{t("signin.continue")}</button>
        </form>
      )}

      <p className="micro" style={{ textAlign: "center" }}>{t("signin.cloud")}</p>
      <AcceptLine />
      {deleted && !error && <p role="alert" className="meta">{t("signin.deleted")}</p>}
      {error && <p role="alert" className="meta" style={{ color: "var(--danger)" }}>{error}</p>}
    </main>
  );
}
